import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { Skeleton } from '@/components/ui/skeleton'
import { AssessmentForm } from './AssessmentForm'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useInvitations } from '../hooks/useInvitations'
import { useAssessments } from '@/features/assessments/hooks/useAssessments'
import { RoleType, AssessmentStatus, AssessmentType, Decision } from '@promo/shared'
import { AssessmentItem } from './AssessmentsSection'
import { InviteAssessorDialog } from './InviteAssessorDialog'
import { InvitationsList } from './InvitationsList'
import { MAX_INVITES_PER_ROLE } from '@promo/shared'
import { AssessmentDecisionChart } from '@/components/custom/AssessmentDecisionChart'
import { useState } from 'react'
import type { SelectOption } from '@/components/ui/editable-select'
import { parseNameFromEmail } from '@promo/shared'

interface BlindAssessmentsSectionProps {
  packetId: string
  readOnly?: boolean
  isEditor?: boolean
  canViewAssessments?: boolean
}

export function BlindAssessmentsSection({ packetId, readOnly = false, isEditor = false, canViewAssessments = true }: BlindAssessmentsSectionProps) {
  const { session } = useAuth()
  const currentUserId = session.user?.id ?? ''

  // Manager/SeniorLeader override: selected assessor
  const [selectedAssessorId, setSelectedAssessorId] = useState<string>('')

  // Count invited assessors for progress
  const { data: assessorInvitations = [] } = useInvitations(
    packetId,
    RoleType.ASSESSOR,
    { enabled: isEditor }
  )
  const invitedCount = assessorInvitations.length

  // Get blinded assessments
  const {
    blinded,
    createAssessment,
    updateAssessment,
    deleteAssessment,
    isLoading,
    error,
    isCreating,
    isUpdating,
    isDeleting,
  } = useAssessments(packetId, canViewAssessments)

  // Build dropdown options for managers (all invites, excluding those who already have assessments)
  const availableAssessors: SelectOption[] = assessorInvitations
    .filter(inv => !blinded.some(a => a.assessorId === inv.inviteeId))
    .map(inv => ({
      value: inv.inviteeId,
      label: parseNameFromEmail(inv.inviteeEmail) || inv.inviteeEmail
    }))

  const userBlind = blinded.find(a => a.assessorId === currentUserId)
  const completedBlind = blinded.filter(a => a.status === AssessmentStatus.COMPLETED).length

  const isEditable = !readOnly

  // Calculate promote/defer counts for the blinded phase
  const promoteCountBlinded = blinded
    .filter(a => a.status === AssessmentStatus.COMPLETED && a.decision === Decision.PROMOTE)
    .length;
  const deferCountBlinded = blinded
    .filter(a => a.status === AssessmentStatus.COMPLETED && a.decision === Decision.DEFER)
    .length;

  const showBlindedChart = completedBlind === invitedCount && invitedCount > 0;

  if (isLoading) {
    return (
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Blinded Assessments</h2>
        <div className="flex items-center justify-center py-8">
          <Spinner className="h-8 w-8" />
          <span className="ml-2">Loading assessments...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mb-6">
        <h2 className="text-xl font-semibold">Blinded Assessments</h2>
        <div className="bg-red-50 text-red-800 p-4 rounded-md mt-4">
          <h3 className="text-lg font-semibold">Error loading assessments</h3>
          <p>{error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <section className="mb-6 space-y-6">
      <div className="flex justify-between lg:justify-start gap-2 items-center mb-6">
        <h2 className="text-xl font-semibold">Blinded Assessments</h2>
        <Badge>{completedBlind} of {invitedCount}</Badge>
      </div>

      {/* Assessment Decision Chart for Blinded Phase */}
      {showBlindedChart && (
        <div className="my-6 flex justify-center">
          <AssessmentDecisionChart 
            promoteCount={promoteCountBlinded}
            deferCount={deferCountBlinded}
          />
        </div>
      )}

      <div>
        <p className="mb-4">
          You <strong>cannot change your decision</strong> once you have submitted your assessment.
        </p>
      </div>

      {blinded.length === 0 ? (
        <Skeleton className="h-4 w-3/4" />
      ) : (
        blinded.map(item => (
          <AssessmentItem
            key={item.id}
            item={item}
            currentUserId={currentUserId}
            isEditable={isEditable && item.assessorId === currentUserId}
            isManager={isEditor}
            updateAssessment={updateAssessment}
            deleteAssessment={deleteAssessment}
            isUpdating={isUpdating}
            isDeleting={isDeleting}
          />
        ))
      )}

      {/* New blind assessment form */}
      {isEditable && (
        (!isEditor && !userBlind) || 
        (isEditor && availableAssessors.length > 0)
      ) && (
        <div className="py-6 space-y-4 border-b last:border-b-0">
          <AssessmentForm
            key={completedBlind}
            onSubmit={(data) =>
              createAssessment({ 
                ...data, 
                packetId: packetId, 
                type: AssessmentType.BLINDED,
                ...(isEditor ? { assessorId: selectedAssessorId } : {})
              }, { onSuccess: () => {} })
            }
            onCancel={() => {}}
            isSubmitting={isCreating}
            isManager={isEditor}
            availableAssessors={availableAssessors}
            selectedAssessorId={selectedAssessorId}
            onAssessorChange={setSelectedAssessorId}
          />
        </div>
      )}

      {/* Manager invitation controls for assessors */}
      {isEditor && (
        <div className="my-6 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-medium">Invitations</h3>
              <Badge variant="secondary">
                {assessorInvitations.length} of {MAX_INVITES_PER_ROLE[RoleType.ASSESSOR]}
              </Badge>
            </div>
            <InviteAssessorDialog packetId={packetId} />
          </div>
          <InvitationsList packetId={packetId} role={RoleType.ASSESSOR} />
        </div>
      )}
    </section>
  )
} 